/**
 * Pure validators usable from both server (route handler / schema) and
 * client (BookingModal) — NO `'server-only'` here, by design. Keeps the
 * client-side disable-Submit-until-valid logic in lockstep with the
 * server's authoritative validation.
 *
 * If you tighten a rule here, the schema picks it up automatically.
 */

const PHONE_DIGITS_ONLY = /^\d{10}$/
const PHONE_DIGITS_NANP_11 = /^1\d{10}$/
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const POSTAL_CA = /^[ABCEGHJ-NPRSTVXY]\d[A-Z]\s?\d[A-Z]\d$/i

export function isValidNanpPhone(raw: string): boolean {
  if (!raw) return false
  const digits = raw.replace(/\D/g, '')
  return PHONE_DIGITS_ONLY.test(digits) || PHONE_DIGITS_NANP_11.test(digits)
}

export function isValidEmail(raw: string): boolean {
  if (!raw) return false
  if (raw.length > 254) return false
  if (raw.includes(' ')) return false
  if (raw.includes('@@')) return false
  if (raw.endsWith('.') || raw.includes('@.')) return false
  return EMAIL.test(raw)
}

export function isValidCanadianPostal(raw: string): boolean {
  if (!raw) return false
  return POSTAL_CA.test(raw)
}

export type ContactFields = {
  name: string
  email: string
  phone: string
  address: string
  city: string
  postal: string
}

export type FieldValidationResult =
  | { ok: true }
  | { ok: false; field: keyof ContactFields; message: string }

export function validateContactFields(f: ContactFields): FieldValidationResult {
  if (!f.name.trim()) {
    return { ok: false, field: 'name', message: 'Please enter your full name.' }
  }
  if (!isValidNanpPhone(f.phone)) {
    return { ok: false, field: 'phone', message: 'Enter a 10-digit Canadian phone number.' }
  }
  if (f.email !== '' && !isValidEmail(f.email)) {
    return { ok: false, field: 'email', message: 'Enter a valid email address, or leave it blank.' }
  }
  if (!f.address.trim() || f.address.trim().length < 3) {
    return { ok: false, field: 'address', message: 'Please enter your street address.' }
  }
  if (!f.city.trim()) {
    return { ok: false, field: 'city', message: 'Please enter your city.' }
  }
  if (!isValidCanadianPostal(f.postal)) {
    return { ok: false, field: 'postal', message: 'Enter a Canadian postal code, e.g. M5V 1A1.' }
  }
  return { ok: true }
}
